import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/auth';
import {
  Sliders, Eye, Save, Check, RefreshCw, ChevronDown, ChevronRight,
  ToggleLeft, ToggleRight, Sparkles, Image, Type, Link as LinkIcon, AlertCircle
} from 'lucide-react';

export interface ContentSection {
  id: string;
  title: string;
  isEnabled: boolean;
  sortOrder: number;
  config: Record<string, any>;
  updatedAt: string;
}

export default function ContentPage() {
  const [sections, setSections]       = useState<ContentSection[]>([]);
  const [loading, setLoading]         = useState(true);
  const [savingId, setSavingId]       = useState<string | null>(null);
  const [savedSuccess, setSavedSuccess] = useState<string | null>(null);
  const [expandedId, setExpandedId]   = useState<string | null>('hero');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ data: ContentSection[] }>('/content/sections');
      setSections(res.data || []);
    } catch (err) {
      console.error('Failed to load content sections:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (section: ContentSection) => {
    const nextState = !section.isEnabled;
    try {
      await apiFetch(`/content/sections/${section.id}`, {
        method: 'PUT',
        body: JSON.stringify({ isEnabled: nextState }),
      });
      setSections(prev => prev.map(s => s.id === section.id ? { ...s, isEnabled: nextState } : s));
    } catch (err) {
      console.error('Toggle error:', err);
    }
  };

  const handleConfigChange = (sectionId: string, key: string, value: any) => {
    setSections(prev => prev.map(s => {
      if (s.id !== sectionId) return s;
      return {
        ...s,
        config: { ...s.config, [key]: value },
      };
    }));
  };

  const handleNestedArrayChange = (sectionId: string, arrayKey: string, index: number, field: string, value: any) => {
    setSections(prev => prev.map(s => {
      if (s.id !== sectionId) return s;
      const arr = [...(s.config[arrayKey] || [])];
      arr[index] = { ...arr[index], [field]: value };
      return {
        ...s,
        config: { ...s.config, [arrayKey]: arr },
      };
    }));
  };

  const handleSaveSection = async (section: ContentSection) => {
    setSavingId(section.id);
    setSavedSuccess(null);
    try {
      await apiFetch(`/content/sections/${section.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          title: section.title,
          isEnabled: section.isEnabled,
          config: section.config,
        }),
      });
      setSavedSuccess(`Saved changes to "${section.title}"! Storefront updated.`);
      setTimeout(() => setSavedSuccess(null), 3500);
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save section changes: ' + String(err));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Storefront Visual Customizer</h1>
          <p style={styles.pageSubtitle}>
            Full owner control: Customize the Hero banner, announcement marquee, category rails, and brand sections in real time.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button style={styles.refreshBtn} onClick={load} title="Reload sections">
            <RefreshCw size={14} /> Refresh
          </button>
          <a
            href="http://localhost:4321"
            target="_blank"
            rel="noreferrer"
            style={styles.previewBtn}
          >
            <Eye size={15} /> Preview Storefront
          </a>
        </div>
      </div>

      {savedSuccess && (
        <div style={styles.successBanner}>
          <Check size={16} /> {savedSuccess}
        </div>
      )}

      {loading ? (
        <div style={styles.loadingBox}>
          <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <p>Loading storefront section configuration…</p>
        </div>
      ) : (
        <div style={styles.sectionsList}>
          {sections.map((section) => {
            const isExpanded = expandedId === section.id;
            return (
              <div key={section.id} style={styles.sectionCard}>
                {/* Section Header Accordion */}
                <div style={styles.cardHeader} onClick={() => setExpandedId(isExpanded ? null : section.id)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button style={styles.expandIconBtn}>
                      {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    <div>
                      <h3 style={styles.sectionTitle}>{section.title}</h3>
                      <span style={styles.sectionKey}>Section ID: <code>{section.id}</code></span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }} onClick={e => e.stopPropagation()}>
                    {/* Visibility Toggle */}
                    <button
                      type="button"
                      style={{
                        ...styles.toggleBtn,
                        color: section.isEnabled ? '#10B981' : '#666',
                      }}
                      onClick={() => handleToggle(section)}
                      title={section.isEnabled ? 'Visible on storefront' : 'Hidden on storefront'}
                    >
                      {section.isEnabled ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 }}>
                          <ToggleRight size={22} color="#10B981" /> Visible
                        </span>
                      ) : (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                          <ToggleLeft size={22} color="#666" /> Hidden
                        </span>
                      )}
                    </button>

                    <button
                      type="button"
                      style={styles.saveBtn}
                      onClick={() => handleSaveSection(section)}
                      disabled={savingId === section.id}
                    >
                      {savingId === section.id ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
                      Save
                    </button>
                  </div>
                </div>

                {/* Section Customization Body */}
                {isExpanded && (
                  <div style={styles.cardBody}>
                    {/* 1. Hero Customizer */}
                    {section.id === 'hero' && (
                      <div style={styles.formGrid}>
                        <div style={styles.fieldFull}>
                          <label style={styles.label}>Eyebrow Badge Text</label>
                          <input
                            type="text"
                            value={section.config.eyebrow || ''}
                            onChange={e => handleConfigChange(section.id, 'eyebrow', e.target.value)}
                            style={styles.input}
                            placeholder="e.g. New Collection 2026"
                          />
                        </div>

                        <div>
                          <label style={styles.label}>Headline Line 1</label>
                          <input
                            type="text"
                            value={section.config.titleLine1 || ''}
                            onChange={e => handleConfigChange(section.id, 'titleLine1', e.target.value)}
                            style={styles.input}
                          />
                        </div>

                        <div>
                          <label style={styles.label}>Italic Emphasis Word (Mustard Highlight)</label>
                          <input
                            type="text"
                            value={section.config.titleItalic || ''}
                            onChange={e => handleConfigChange(section.id, 'titleItalic', e.target.value)}
                            style={styles.input}
                          />
                        </div>

                        <div>
                          <label style={styles.label}>Headline Line 2</label>
                          <input
                            type="text"
                            value={section.config.titleLine2 || ''}
                            onChange={e => handleConfigChange(section.id, 'titleLine2', e.target.value)}
                            style={styles.input}
                          />
                        </div>

                        <div>
                          <label style={styles.label}>Image Caption Label</label>
                          <input
                            type="text"
                            value={section.config.imageCaption || ''}
                            onChange={e => handleConfigChange(section.id, 'imageCaption', e.target.value)}
                            style={styles.input}
                          />
                        </div>

                        <div style={styles.fieldFull}>
                          <label style={styles.label}>Editorial Narrative Body</label>
                          <textarea
                            rows={3}
                            value={section.config.body || ''}
                            onChange={e => handleConfigChange(section.id, 'body', e.target.value)}
                            style={styles.textarea}
                          />
                        </div>

                        <div>
                          <label style={styles.label}>Primary Button Label</label>
                          <input
                            type="text"
                            value={section.config.primaryCtaText || ''}
                            onChange={e => handleConfigChange(section.id, 'primaryCtaText', e.target.value)}
                            style={styles.input}
                          />
                        </div>

                        <div>
                          <label style={styles.label}>Primary Button URL</label>
                          <input
                            type="text"
                            value={section.config.primaryCtaHref || ''}
                            onChange={e => handleConfigChange(section.id, 'primaryCtaHref', e.target.value)}
                            style={styles.input}
                          />
                        </div>

                        <div>
                          <label style={styles.label}>Secondary Button Label</label>
                          <input
                            type="text"
                            value={section.config.secondaryCtaText || ''}
                            onChange={e => handleConfigChange(section.id, 'secondaryCtaText', e.target.value)}
                            style={styles.input}
                          />
                        </div>

                        <div>
                          <label style={styles.label}>Secondary Button URL</label>
                          <input
                            type="text"
                            value={section.config.secondaryCtaHref || ''}
                            onChange={e => handleConfigChange(section.id, 'secondaryCtaHref', e.target.value)}
                            style={styles.input}
                          />
                        </div>

                        <div style={styles.fieldFull}>
                          <label style={styles.label}>Editorial Hero Image URL (Master Rendition)</label>
                          <input
                            type="text"
                            value={section.config.imageUrl || ''}
                            onChange={e => handleConfigChange(section.id, 'imageUrl', e.target.value)}
                            style={styles.input}
                          />
                          {section.config.imageUrl && (
                            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                              <img
                                src={section.config.imageUrl}
                                alt="Hero preview"
                                style={{ width: 80, height: 100, objectFit: 'cover', borderRadius: 4, border: '1px solid #444' }}
                              />
                              <span style={{ fontSize: 12, color: '#8A8A8E' }}>Live image preview rendered above</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* 2. Announcement Bar Customizer */}
                    {section.id === 'announcement_bar' && (
                      <div style={styles.formGrid}>
                        <div style={styles.fieldFull}>
                          <label style={styles.label}>Announcement Text</label>
                          <input
                            type="text"
                            value={section.config.text || ''}
                            onChange={e => handleConfigChange(section.id, 'text', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.label}>Action Link Label</label>
                          <input
                            type="text"
                            value={section.config.linkText || ''}
                            onChange={e => handleConfigChange(section.id, 'linkText', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.label}>Action Link Destination</label>
                          <input
                            type="text"
                            value={section.config.linkHref || ''}
                            onChange={e => handleConfigChange(section.id, 'linkHref', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                      </div>
                    )}

                    {/* 3. Marquee Strip */}
                    {section.id === 'marquee' && (
                      <div style={styles.formGrid}>
                        <div style={styles.fieldFull}>
                          <label style={styles.label}>Ticker Items / Marquee Copy</label>
                          <input
                            type="text"
                            value={section.config.text || ''}
                            onChange={e => handleConfigChange(section.id, 'text', e.target.value)}
                            style={styles.input}
                            placeholder="e.g. Free Shipping · Sustainably Made · Easy Returns"
                          />
                        </div>
                      </div>
                    )}

                    {/* 4. Featured Collection */}
                    {section.id === 'featured_collection' && (
                      <div style={styles.formGrid}>
                        <div>
                          <label style={styles.label}>Heading Title</label>
                          <input
                            type="text"
                            value={section.config.title || ''}
                            onChange={e => handleConfigChange(section.id, 'title', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.label}>Link Text</label>
                          <input
                            type="text"
                            value={section.config.linkText || ''}
                            onChange={e => handleConfigChange(section.id, 'linkText', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.label}>Collection URL</label>
                          <input
                            type="text"
                            value={section.config.linkHref || ''}
                            onChange={e => handleConfigChange(section.id, 'linkHref', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.label}>Max Display Products</label>
                          <input
                            type="number"
                            value={section.config.limit || 4}
                            onChange={e => handleConfigChange(section.id, 'limit', Number(e.target.value))}
                            style={styles.input}
                          />
                        </div>
                      </div>
                    )}

                    {/* 5. Essential Silhouettes */}
                    {section.id === 'essential_silhouettes' && (
                      <div style={styles.formGrid}>
                        <div>
                          <label style={styles.label}>Eyebrow Subtitle</label>
                          <input
                            type="text"
                            value={section.config.eyebrow || ''}
                            onChange={e => handleConfigChange(section.id, 'eyebrow', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.label}>Section Title</label>
                          <input
                            type="text"
                            value={section.config.title || ''}
                            onChange={e => handleConfigChange(section.id, 'title', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.label}>Link Destination</label>
                          <input
                            type="text"
                            value={section.config.linkHref || ''}
                            onChange={e => handleConfigChange(section.id, 'linkHref', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                      </div>
                    )}

                    {/* 6. Brand Values Strip */}
                    {section.id === 'brand_values' && (
                      <div>
                        <p style={{ fontSize: 13, color: '#AAA', marginBottom: 12 }}>
                          Atelier craftsmanship & sustainability value pillars:
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                          {(section.config.items || []).map((val: any, idx: number) => (
                            <div key={idx} style={styles.subItemCard}>
                              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                <input
                                  type="text"
                                  value={val.icon || ''}
                                  onChange={e => handleNestedArrayChange(section.id, 'items', idx, 'icon', e.target.value)}
                                  style={{ ...styles.input, width: 44, textAlign: 'center' }}
                                />
                                <input
                                  type="text"
                                  value={val.title || ''}
                                  onChange={e => handleNestedArrayChange(section.id, 'items', idx, 'title', e.target.value)}
                                  style={{ ...styles.input, flex: 1 }}
                                />
                              </div>
                              <textarea
                                rows={2}
                                value={val.body || ''}
                                onChange={e => handleNestedArrayChange(section.id, 'items', idx, 'body', e.target.value)}
                                style={styles.textarea}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 7. Shop by Category Rail */}
                    {section.id === 'shop_by_category' && (
                      <div>
                        <div style={{ marginBottom: 16 }}>
                          <label style={styles.label}>Rail Title</label>
                          <input
                            type="text"
                            value={section.config.title || ''}
                            onChange={e => handleConfigChange(section.id, 'title', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                          {(section.config.categories || []).map((cat: any, idx: number) => (
                            <div key={idx} style={styles.subItemCard}>
                              <label style={styles.label}>Category {idx + 1}</label>
                              <input
                                type="text"
                                value={cat.label || ''}
                                onChange={e => handleNestedArrayChange(section.id, 'categories', idx, 'label', e.target.value)}
                                style={{ ...styles.input, marginBottom: 6 }}
                                placeholder="Label"
                              />
                              <input
                                type="text"
                                value={cat.href || ''}
                                onChange={e => handleNestedArrayChange(section.id, 'categories', idx, 'href', e.target.value)}
                                style={{ ...styles.input, marginBottom: 6 }}
                                placeholder="Link URL"
                              />
                              <input
                                type="text"
                                value={cat.img || ''}
                                onChange={e => handleNestedArrayChange(section.id, 'categories', idx, 'img', e.target.value)}
                                style={styles.input}
                                placeholder="Photo URL"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 8. Newsletter Subscription */}
                    {section.id === 'newsletter' && (
                      <div style={styles.formGrid}>
                        <div>
                          <label style={styles.label}>Headline</label>
                          <input
                            type="text"
                            value={section.config.title || ''}
                            onChange={e => handleConfigChange(section.id, 'title', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.label}>Subscribe Button Text</label>
                          <input
                            type="text"
                            value={section.config.buttonText || ''}
                            onChange={e => handleConfigChange(section.id, 'buttonText', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                        <div style={styles.fieldFull}>
                          <label style={styles.label}>Subtitle Text</label>
                          <input
                            type="text"
                            value={section.config.body || ''}
                            onChange={e => handleConfigChange(section.id, 'body', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                      </div>
                    )}

                    {/* 9. Site Footer */}
                    {section.id === 'footer' && (
                      <div style={styles.formGrid}>
                        <div>
                          <label style={styles.label}>Brand Tagline</label>
                          <input
                            type="text"
                            value={section.config.tagline || ''}
                            onChange={e => handleConfigChange(section.id, 'tagline', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.label}>Copyright Notice</label>
                          <input
                            type="text"
                            value={section.config.copyrightText || ''}
                            onChange={e => handleConfigChange(section.id, 'copyrightText', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.label}>Client Care / Support Email</label>
                          <input
                            type="email"
                            value={section.config.supportEmail || ''}
                            onChange={e => handleConfigChange(section.id, 'supportEmail', e.target.value)}
                            style={styles.input}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page:           { maxWidth: 1100, margin: '0 auto', color: '#E5E5E5', width: '100%' },
  pageHeader:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  pageTitle:      { fontSize: 24, fontWeight: 600, color: '#FFF', marginBottom: 4, fontFamily: "'Playfair Display', serif" },
  pageSubtitle:   { fontSize: 13, color: '#8A8A8E', maxWidth: 650 },
  refreshBtn:     { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#222', border: '1px solid #333', color: '#CCC', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
  previewBtn:     { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#C9A84C', color: '#1A1A1A', borderRadius: 6, fontSize: 13, fontWeight: 600, textDecoration: 'none' },
  successBanner:  { padding: '12px 16px', background: 'rgba(16,185,129,0.15)', border: '1px solid #10B981', color: '#10B981', borderRadius: 6, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500 },
  loadingBox:     { padding: 60, textAlign: 'center', color: '#8A8A8E', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
  sectionsList:   { display: 'flex', flexDirection: 'column', gap: 16 },
  sectionCard:    { background: '#1E1E22', border: '1px solid #333', borderRadius: 8, overflow: 'hidden' },
  cardHeader:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', cursor: 'pointer', background: '#202026', borderBottom: '1px solid #2C2C32', userSelect: 'none', flexWrap: 'wrap', gap: 10 },
  expandIconBtn:  { background: 'transparent', border: 'none', color: '#C9A84C', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 },
  sectionTitle:   { margin: 0, fontSize: 15, fontWeight: 600, color: '#FFF' },
  sectionKey:     { fontSize: 11, color: '#777' },
  toggleBtn:      { background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  saveBtn:        { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: '#C9A84C', border: 'none', color: '#000', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  cardBody:       { padding: 20, background: '#18181C' },
  formGrid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 },
  fieldFull:      { gridColumn: '1 / -1' },
  label:          { display: 'block', fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 6 },
  input:          { width: '100%', background: '#141416', border: '1px solid #333', borderRadius: 6, padding: '8px 12px', color: '#FFF', fontSize: 13, outline: 'none' },
  textarea:       { width: '100%', background: '#141416', border: '1px solid #333', borderRadius: 6, padding: '8px 12px', color: '#FFF', fontSize: 13, outline: 'none', resize: 'vertical' },
  subItemCard:    { background: '#202024', border: '1px solid #333', borderRadius: 6, padding: 12 },
};
